import { useState, useEffect, useRef, useCallback } from 'react';
import { getAuth } from 'firebase/auth';

interface LogEntry {
  id: string;
  timestamp: string;
  severity: 'DEBUG' | 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';
  service: 'api' | 'worker' | 'functions' | 'frontend' | 'openfec' | 'stripe' | 'perplexity';
  message: string;
  metadata?: Record<string, unknown>;
}

interface BackendStatus {
  status: string;
  database: { connected: boolean; latency_ms?: number };
  stripe: { configured: boolean };
  perplexity: { configured: boolean; enabled: boolean };
  firebase: { configured: boolean };
  secrets: Record<string, boolean>;
}

const API_BASE = import.meta.env.VITE_API_URL || 'https://republic-atlas-api-946143380554.us-central1.run.app/api/v1';

// Capture console logs
const capturedLogs: LogEntry[] = [];
const originalConsole = {
  log: console.log,
  warn: console.warn,
  error: console.error,
  info: console.info,
  debug: console.debug,
};

// Intercept console methods
(['log', 'warn', 'error', 'info', 'debug'] as const).forEach((method) => {
  const severity = method === 'warn' ? 'WARNING' : method === 'error' ? 'ERROR' : method === 'debug' ? 'DEBUG' : 'INFO';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (console as any)[method] = (...args: unknown[]) => {
    capturedLogs.unshift({
      id: `console-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      severity: severity as LogEntry['severity'],
      service: 'frontend',
      message: args.map(a => typeof a === 'object' ? JSON.stringify(a) : String(a)).join(' '),
    });
    if (capturedLogs.length > 500) capturedLogs.pop();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (originalConsole as any)[method](...args);
  };
});

export const LiveLogsViewer = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [backendStatus, setBackendStatus] = useState<BackendStatus | null>(null);
  const [backendErrors, setBackendErrors] = useState<LogEntry[]>([]);
  const [isStreaming, setIsStreaming] = useState(true);
  const [filters, setFilters] = useState({
    severity: 'all' as 'all' | LogEntry['severity'],
    service: 'all' as 'all' | LogEntry['service'],
    search: '',
  });
  const [selectedLog, setSelectedLog] = useState<LogEntry | null>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [activeSource, setActiveSource] = useState<'frontend' | 'backend'>('frontend');
  const logsEndRef = useRef<HTMLDivElement>(null);

  // Get auth token for backend requests
  const getAuthToken = useCallback(async () => {
    try {
      const user = getAuth().currentUser;
      if (user) {
        return await user.getIdToken(true);
      }
    } catch (e) {
      console.error('Failed to get auth token:', e);
    }
    return null;
  }, []);

  // Fetch backend status
  const fetchBackendStatus = useCallback(async () => {
    try {
      const token = await getAuthToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(`${API_BASE}/debug/status`, { headers });
      if (response.ok) {
        const data = await response.json();
        setBackendStatus(data);
      }
    } catch (e) {
      // Silently fail - backend may not be accessible
    }
  }, [getAuthToken]);

  // Fetch backend errors
  const fetchBackendErrors = useCallback(async () => {
    try {
      const token = await getAuthToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(`${API_BASE}/debug/errors`, { headers });
      if (response.ok) {
        const data = await response.json();
        if (data.errors) {
          setBackendErrors(data.errors.map((e: Record<string, unknown>, i: number) => ({
            id: `backend-${i}`,
            timestamp: e.timestamp as string || new Date().toISOString(),
            severity: 'ERROR' as const,
            service: 'api' as const,
            message: e.message as string || JSON.stringify(e),
            metadata: e,
          })));
        }
      }
    } catch (e) {
      // Silently fail
    }
  }, [getAuthToken]);

  // Load initial data
  useEffect(() => {
    setLogs([...capturedLogs]);
    fetchBackendStatus();
    fetchBackendErrors();
  }, [fetchBackendStatus, fetchBackendErrors]);

  // Stream frontend logs
  useEffect(() => {
    if (!isStreaming) return;

    const interval = setInterval(() => {
      setLogs([...capturedLogs]);
    }, 1000);

    return () => clearInterval(interval);
  }, [isStreaming]);

  // Refresh backend data periodically
  useEffect(() => {
    if (!isStreaming || activeSource !== 'backend') return;

    const interval = setInterval(() => {
      fetchBackendStatus();
      fetchBackendErrors();
    }, 5000);

    return () => clearInterval(interval);
  }, [isStreaming, activeSource, fetchBackendStatus, fetchBackendErrors]);

  // Auto-scroll
  useEffect(() => {
    if (autoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, autoScroll]);

  const displayLogs = activeSource === 'frontend' ? logs : backendErrors;
  
  const filteredLogs = displayLogs.filter(log => {
    if (filters.severity !== 'all' && log.severity !== filters.severity) return false;
    if (filters.service !== 'all' && log.service !== filters.service) return false;
    if (filters.search && !log.message.toLowerCase().includes(filters.search.toLowerCase())) return false;
    return true;
  });

  const getSeverityColor = (severity: LogEntry['severity']) => {
    switch (severity) {
      case 'DEBUG': return 'text-gray-500';
      case 'INFO': return 'text-blue-400';
      case 'WARNING': return 'text-yellow-400';
      case 'ERROR': return 'text-red-400';
      case 'CRITICAL': return 'text-red-600 font-bold';
    }
  };

  const getServiceColor = (service: LogEntry['service']) => {
    switch (service) {
      case 'api': return 'bg-green-600/30 text-green-400';
      case 'worker': return 'bg-purple-600/30 text-purple-400';
      case 'functions': return 'bg-orange-600/30 text-orange-400';
      case 'frontend': return 'bg-blue-600/30 text-blue-400';
      case 'openfec': return 'bg-cyan-600/30 text-cyan-400';
      case 'stripe': return 'bg-indigo-600/30 text-indigo-400';
      case 'perplexity': return 'bg-pink-600/30 text-pink-400';
    }
  };

  const errorCount = displayLogs.filter(l => l.severity === 'ERROR' || l.severity === 'CRITICAL').length;
  const warningCount = displayLogs.filter(l => l.severity === 'WARNING').length;

  return (
    <div className="space-y-4">
      {/* Backend Status Summary */}
      {backendStatus && (
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-lg p-3 border border-gray-600">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-300">Backend Status</span>
            <span className={`text-xs px-2 py-0.5 rounded ${backendStatus.status === 'healthy' ? 'bg-green-600/30 text-green-400' : 'bg-red-600/30 text-red-400'}`}>
              {backendStatus.status}
            </span>
          </div>
          <div className="grid grid-cols-4 gap-2 text-xs">
            <div className="flex items-center gap-1">
              <span className={backendStatus.database?.connected ? 'text-green-400' : 'text-red-400'}>●</span>
              <span className="text-gray-400">DB</span>
              {backendStatus.database?.latency_ms && (
                <span className="text-gray-500">{backendStatus.database.latency_ms}ms</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <span className={backendStatus.stripe?.configured ? 'text-green-400' : 'text-yellow-400'}>●</span>
              <span className="text-gray-400">Stripe</span>
            </div>
            <div className="flex items-center gap-1">
              <span className={backendStatus.perplexity?.enabled ? 'text-green-400' : 'text-yellow-400'}>●</span>
              <span className="text-gray-400">Perplexity</span>
            </div>
            <div className="flex items-center gap-1">
              <span className={backendStatus.firebase?.configured ? 'text-green-400' : 'text-yellow-400'}>●</span>
              <span className="text-gray-400">Firebase</span>
            </div>
          </div>
        </div>
      )}

      {/* Source Toggle */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setActiveSource('frontend')}
          className={`px-3 py-1 rounded text-xs font-medium ${
            activeSource === 'frontend' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'
          }`}
        >
          Frontend Logs ({logs.length})
        </button>
        <button
          onClick={() => setActiveSource('backend')}
          className={`px-3 py-1 rounded text-xs font-medium ${
            activeSource === 'backend' ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300'
          }`}
        >
          Backend Errors ({backendErrors.length})
        </button>
      </div>

      {/* Log Summary */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-lg p-3 border border-gray-600">
        <p className="text-sm">
          <span className="text-gray-300 font-medium">Log Stream:</span>{' '}
          <span className="text-gray-400">
            {isStreaming ? (
              <span className="text-green-400">● Live</span>
            ) : (
              <span className="text-gray-500">○ Paused</span>
            )}
            {' • '}{filteredLogs.length} entries
            {errorCount > 0 && <>, <span className="text-red-400">{errorCount} errors</span></>}
            {warningCount > 0 && <>, <span className="text-yellow-400">{warningCount} warnings</span></>}
          </span>
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setIsStreaming(!isStreaming)}
          className={`px-3 py-1 rounded text-xs font-medium ${
            isStreaming
              ? 'bg-red-600 hover:bg-red-700 text-white'
              : 'bg-green-600 hover:bg-green-700 text-white'
          }`}
        >
          {isStreaming ? '⏸ Pause' : '▶ Stream'}
        </button>
        
        <select
          value={filters.severity}
          onChange={(e) => setFilters(f => ({ ...f, severity: e.target.value as typeof filters.severity }))}
          className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs"
        >
          <option value="all">All Levels</option>
          <option value="DEBUG">DEBUG</option>
          <option value="INFO">INFO</option>
          <option value="WARNING">WARNING</option>
          <option value="ERROR">ERROR</option>
          <option value="CRITICAL">CRITICAL</option>
        </select>

        <select
          value={filters.service}
          onChange={(e) => setFilters(f => ({ ...f, service: e.target.value as typeof filters.service }))}
          className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs"
        >
          <option value="all">All Services</option>
          <option value="api">API</option>
          <option value="worker">Worker</option>
          <option value="functions">Functions</option>
          <option value="frontend">Frontend</option>
          <option value="openfec">OpenFEC</option>
          <option value="stripe">Stripe</option>
          <option value="perplexity">Perplexity</option>
        </select>

        <input
          type="text"
          placeholder="Search logs..."
          value={filters.search}
          onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
          className="flex-1 min-w-[120px] bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs"
        />

        <label className="flex items-center gap-1 text-xs text-gray-400">
          <input
            type="checkbox"
            checked={autoScroll}
            onChange={(e) => setAutoScroll(e.target.checked)}
            className="rounded"
          />
          Auto-scroll
        </label>

        <button
          onClick={() => {
            capturedLogs.length = 0;
            setLogs([]);
          }}
          className="bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded text-xs"
        >
          Clear
        </button>
      </div>

      {/* Log List */}
      <div className="bg-gray-900 rounded-lg overflow-hidden">
        <div className="max-h-72 overflow-y-auto font-mono text-[11px]">
          {filteredLogs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No logs matching filters</div>
          ) : (
            filteredLogs.map((log) => (
              <div
                key={log.id}
                onClick={() => setSelectedLog(selectedLog?.id === log.id ? null : log)}
                className={`flex items-start gap-2 px-2 py-1 border-b border-gray-800 cursor-pointer hover:bg-gray-800/50 ${
                  selectedLog?.id === log.id ? 'bg-gray-800' : ''
                } ${log.severity === 'ERROR' || log.severity === 'CRITICAL' ? 'bg-red-900/20' : ''}`}
              >
                <span className="text-gray-600 w-20 flex-shrink-0">
                  {new Date(log.timestamp).toLocaleTimeString()}
                </span>
                <span className={`w-16 flex-shrink-0 ${getSeverityColor(log.severity)}`}>
                  {log.severity}
                </span>
                <span className={`px-1 rounded text-[10px] ${getServiceColor(log.service)}`}>
                  {log.service}
                </span>
                <span className="text-gray-300 flex-1 truncate">{log.message}</span>
              </div>
            ))
          )}
          <div ref={logsEndRef} />
        </div>
      </div>

      {/* Selected Log Details */}
      {selectedLog && (
        <div className="bg-gray-800 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-300">Log Details</h4>
            <button
              onClick={() => setSelectedLog(null)}
              className="text-gray-500 hover:text-white text-xs"
            >
              ✕
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs mb-2">
            <div className="bg-gray-900 rounded p-2">
              <span className="text-gray-500">Timestamp:</span>
              <span className="ml-2 text-gray-300">{new Date(selectedLog.timestamp).toLocaleString()}</span>
            </div>
            <div className="bg-gray-900 rounded p-2">
              <span className="text-gray-500">Severity:</span>
              <span className={`ml-2 ${getSeverityColor(selectedLog.severity)}`}>{selectedLog.severity}</span>
            </div>
            <div className="bg-gray-900 rounded p-2">
              <span className="text-gray-500">Service:</span>
              <span className="ml-2 text-gray-300">{selectedLog.service}</span>
            </div>
            <div className="bg-gray-900 rounded p-2">
              <span className="text-gray-500">ID:</span>
              <span className="ml-2 text-gray-300 font-mono">{selectedLog.id}</span>
            </div>
          </div>
          <div className="bg-gray-900 rounded p-2">
            <span className="text-gray-500 text-xs">Message:</span>
            <p className="text-gray-300 text-sm mt-1 break-all">{selectedLog.message}</p>
          </div>
          {selectedLog.metadata && (
            <div className="bg-gray-900 rounded p-2 mt-2">
              <span className="text-gray-500 text-xs">Metadata:</span>
              <pre className="text-[10px] text-gray-400 mt-1 overflow-auto max-h-32">
                {JSON.stringify(selectedLog.metadata, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}

      {/* Quick Links */}
      <div className="flex gap-2 text-xs">
        <a
          href="https://console.cloud.google.com/logs/query?project=republic-atlas"
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 text-center bg-gray-700 hover:bg-gray-600 py-2 rounded text-blue-400"
        >
          📊 Cloud Logging Console
        </a>
        <a
          href="https://console.cloud.google.com/run?project=republic-atlas"
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 text-center bg-gray-700 hover:bg-gray-600 py-2 rounded text-blue-400"
        >
          🚀 Cloud Run Logs
        </a>
      </div>
    </div>
  );
};
